import { memo, useCallback } from 'react'

import { useDispatch } from '@tabeeb/store'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@collaborate-health/uikit'
import { trainingMaterialTitleFormatter } from '@tabeeb/shared/utils/text'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { ContentTrainingMaterialResponse } from '@tabeeb/contracts/microservices/swagger-article-service'

import { useContentTrainingMaterial, useTrainingMaterial } from '../../hooks'

import ArticleContent, { ArticleContentSkeleton } from '../ArticleContent'
import { ArticleNameSkeleton } from '../ArticleName'

export interface ContentTrainingMaterialDialogProps {
  open: boolean
  contentTrainingMaterial?: ContentTrainingMaterialResponse
  onClose: () => void
  onUpdated: () => void
}

const ContentTrainingMaterialDialog: React.FC<ContentTrainingMaterialDialogProps> = ({
  open,
  contentTrainingMaterial,
  onClose,
  onUpdated,
}) => {
  const dispatch = useDispatch()

  const onError = useCallback(() => {
    dispatch(
      onAddErrorNotification({ message: trainingMaterialTitleFormatter.format('Failed to load training material') })
    )
    onClose()
  }, [dispatch, onClose])

  const onMarkedAsRead = useCallback(() => {
    onUpdated()
    onClose()
  }, [onUpdated, onClose])

  const { isSubmitInProgress, onMarkAsRead } = useContentTrainingMaterial({
    id: contentTrainingMaterial?.Id,
    onMarkedAsRead,
  })

  const { loading, trainingMaterial } = useTrainingMaterial({
    id: open ? contentTrainingMaterial?.TrainingMaterial?.Id : undefined,
    onError,
  })

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth='md'
      PaperProps={{ sx: { height: '100%' } }}
      TransitionProps={{ unmountOnExit: true }}
      onClose={onClose}
    >
      <DialogTitle display='flex' alignItems='center' sx={{ paddingBottom: 0 }}>
        {loading ? <ArticleNameSkeleton /> : trainingMaterial?.Name || 'Training Material'}
      </DialogTitle>
      <DialogContent sx={{ paddingInline: 0, display: 'flex', flexDirection: 'column' }}>
        {loading ? <ArticleContentSkeleton /> : <ArticleContent content={trainingMaterial?.Content} />}
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={onClose}>
          Close
        </Button>
        {contentTrainingMaterial?.Assigned && !contentTrainingMaterial?.Read && (
          <Button
            loading={isSubmitInProgress}
            disabled={loading}
            color='primary'
            variant='contained'
            onClick={() => onMarkAsRead()}
          >
            Mark as read
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default memo(ContentTrainingMaterialDialog)

